/**
 * reCAPTCHA Handler - Utility module to handle reCAPTCHA v3 execution
 *
 * This module provides a reusable function to execute reCAPTCHA v3 before form submission.
 * It handles token generation, error cases, and fallback scenarios.
 *
 * @module recaptcha-handler
 */

/**
 * Executes reCAPTCHA v3 and calls the callback with the result
 *
 * @param {Object} options - Configuration options
 * @param {HTMLFormElement} options.form - The form element
 * @param {string} options.tokenFieldId - The ID of the hidden input field to store the token
 * @param {string} [options.defaultAction] - Default action name if not specified in form attribute
 * @param {Function} options.onSuccess - Callback function called on successful token generation
 * @param {Function} options.onError - Callback function called on error
 *
 * @example
 * executeRecaptcha({
 *   form: formElement,
 *   tokenFieldId: 'g-recaptcha-response-basic',
 *   defaultAction: 'contact_form',
 *   onSuccess: () => submitForm(),
 *   onError: (error) => handleError(error)
 * });
 */
export function executeRecaptcha({ form, tokenFieldId, defaultAction, onSuccess, onError }) {
    // Check if reCAPTCHA is available
    //console.log('[reCAPTCHA] executeRecaptcha called, grecaptcha available:', typeof grecaptcha !== 'undefined');
    if (typeof grecaptcha === 'undefined') {
        // If reCAPTCHA not available, call success callback without token (fallback)
        console.warn('reCAPTCHA not available, proceeding without token');
        onSuccess();
        return;
    }

    // Get configuration from form attributes
    const siteKey = form.getAttribute('data-recaptcha-site-key');
    const action = form.getAttribute('data-recaptcha-action') || defaultAction;

    // If no site key configured, proceed without reCAPTCHA (fallback)
    if (!siteKey) {
        console.warn('reCAPTCHA site key not found, proceeding without token');
        onSuccess();
        return;
    }

    // Execute reCAPTCHA v3
    grecaptcha.execute(siteKey, { action: action })
        .then(function(token) {
            // Assign token to hidden field
            const tokenField = document.getElementById(tokenFieldId);
            if (tokenField) {
                tokenField.value = token;
            } else {
                console.warn(`Token field with ID "${tokenFieldId}" not found`);
            }

            // Call success callback
            onSuccess(token);
        })
        .catch(function(error) {
            console.error('reCAPTCHA error:', error);

            // Call error callback
            onError(error);
        });
}

/**
 * Default export for backwards compatibility
 */
export default {
    executeRecaptcha
};
